import { useState, useEffect } from 'react';
import { createPublicClient, http } from 'viem';
import { BLESSNET_CHAIN_ID, BLESSNET_RPC } from '@/features/bridge/constants/bridge.constant';
import { blessnetMainnet, blessnetSepolia } from '@/config/bless-chain';
import { nftImageCache } from './nft-image-cache.util';

/**
 * NFT Image Fallback Utilities
 * Provides robust image loading with multiple fallback strategies
 */

/**
 * Converts IPFS URLs to HTTP gateway URLs with multiple fallback options
 */
export function convertIpfsToHttp(ipfsUrl: string): string[] {
  if (!ipfsUrl) return [];

  // If it's already an HTTP URL, return as-is
  if (ipfsUrl.startsWith('http')) {
    return [ipfsUrl];
  }

  // Extract IPFS hash from various IPFS URL formats
  let hash = '';
  if (ipfsUrl.startsWith('ipfs://')) {
    hash = ipfsUrl.replace('ipfs://', '');
  } else if (ipfsUrl.startsWith('ipfs/')) {
    hash = ipfsUrl.replace('ipfs/', '');
  } else if (ipfsUrl.includes('/ipfs/')) {
    hash = ipfsUrl.split('/ipfs/')[1];
  } else {
    // Assume it's already a hash
    hash = ipfsUrl;
  }

  // Return multiple gateway options for better reliability
  return [
    `https://ipfs.io/ipfs/${hash}`,
    `https://gateway.ipfs.io/ipfs/${hash}`,
    `https://cloudflare-ipfs.com/ipfs/${hash}`,
    `https://gateway.pinata.cloud/ipfs/${hash}`,
    `https://dweb.link/ipfs/${hash}`,
  ];
}

/**
 * Fetches tokenURI (ERC721) or uri (ERC1155) from contract and generates fallback URL by replacing token ID
 */
async function generateBlessnetContractFallback(
  contractAddress: string,
  tokenId: string,
  chainId: number,
  tokenType?: string
): Promise<string | null> {
  console.log(`🚀 generateBlessnetContractFallback called:`, {
    contractAddress,
    tokenId,
    chainId,
    tokenType
  });

  // Check cache first
  const cachedUrl = nftImageCache.get(contractAddress, tokenId, chainId);
  if (cachedUrl) {
    console.log(`💾 Using cached URL for ${contractAddress}/${tokenId}:`, cachedUrl);
    return cachedUrl;
  }

  try {
    // Use the correct chain configuration
    const chain = chainId === 45513 ? blessnetMainnet : blessnetSepolia;

    // Create a public client for the Blessnet chain with timeout
    const client = createPublicClient({
      chain,
      transport: http(BLESSNET_RPC, {
        timeout: 5000 // 5 second timeout
      })
    });

    // Try to get tokenURI (ERC721) or uri (ERC1155) for a template token (try token ID 1, then 0)
    let templateTokenURI: string | null = null;

    // Use different ABI method based on token type
    // Handle various token type formats
    const normalizedTokenType = tokenType?.toLowerCase?.() || '';
    const isERC1155 = normalizedTokenType === 'erc1155' ||
                      normalizedTokenType === 'erc-1155' ||
                      tokenType === 'ERC1155' ||
                      tokenType === 'ERC-1155';
    const methodName = isERC1155 ? 'uri' : 'tokenURI';

    // Debug logging for troubleshooting - log for ALL contracts to see what's happening
    console.log(`🔍 DEBUG - Token type detection for ${contractAddress}:`, {
      tokenType,
      isERC1155,
      methodName,
      tokenTypeType: typeof tokenType,
      normalizedTokenType
    });

    const tokenAbi = [
      {
        inputs: [{ name: 'tokenId', type: 'uint256' }],
        name: methodName,
        outputs: [{ name: '', type: 'string' }],
        stateMutability: 'view',
        type: 'function'
      }
    ];

    // Try token ID 1 first, then 0 if that fails
    for (const templateId of [1, 0]) {
      try {
        templateTokenURI = await client.readContract({
          address: contractAddress as `0x${string}`,
          abi: tokenAbi,
          functionName: methodName,
          args: [BigInt(templateId)]
        }) as string;

        if (templateTokenURI) {
          break;
        }
      } catch (error) {
        console.warn(`Failed to get ${methodName} for template ID ${templateId}:`, error);
        continue;
      }
    }

    if (!templateTokenURI) {
      return null;
    }

        // Fetch the metadata JSON to get the actual image URL
    let metadataResponse: Response;
    let metadata: any;

    try {
      // Handle {id} placeholder in metadata URL for ERC1155
      let metadataUrl = templateTokenURI;
      if (isERC1155 && templateTokenURI.includes('{id}')) {
        // Try uint256 formatted token ID first (64-character hex, left-padded)
        const tokenIdBigInt = BigInt(tokenId);
        const paddedTokenId = tokenIdBigInt.toString(16).padStart(64, '0');
        const paddedUrl = templateTokenURI.replace('{id}', paddedTokenId);

        console.log(`🔄 ERC1155 metadata URL {id} replacement - trying padded first:`, {
          originalUrl: templateTokenURI,
          tokenId,
          paddedTokenId,
          paddedUrl
        });

        // Try padded version first
        try {
          if (paddedUrl.startsWith('ipfs://')) {
            const ipfsUrls = convertIpfsToHttp(paddedUrl);
            metadataUrl = ipfsUrls[0];
          } else {
            metadataUrl = paddedUrl;
          }

          console.log(`🌐 ERC1155 requesting metadata (padded):`, {
            contractAddress,
            tokenId,
            requestUrl: metadataUrl
          });

          metadataResponse = await fetch(metadataUrl, {
            signal: AbortSignal.timeout(5000)
          });

          if (!metadataResponse.ok) {
            throw new Error(`Padded version failed: ${metadataResponse.status}`);
          }
        } catch (paddedError) {
          console.log(`🔄 Padded version failed, trying plain token ID:`, paddedError);

          // If padded version fails, try with plain token ID
          const plainUrl = templateTokenURI.replace('{id}', tokenId);

          console.log(`🔄 ERC1155 metadata URL {id} replacement - trying plain:`, {
            originalUrl: templateTokenURI,
            tokenId,
            plainUrl
          });

          if (plainUrl.startsWith('ipfs://')) {
            const ipfsUrls = convertIpfsToHttp(plainUrl);
            metadataUrl = ipfsUrls[0];
          } else {
            metadataUrl = plainUrl;
          }

          console.log(`🌐 ERC1155 requesting metadata (plain):`, {
            contractAddress,
            tokenId,
            requestUrl: metadataUrl
          });

          metadataResponse = await fetch(metadataUrl, {
            signal: AbortSignal.timeout(5000)
          });

          if (!metadataResponse.ok) {
            throw new Error(`Both padded and plain versions failed: ${metadataResponse.status}`);
          }
        }
      } else {
        // No {id} replacement needed
        if (templateTokenURI.startsWith('ipfs://')) {
          const ipfsUrls = convertIpfsToHttp(templateTokenURI);
          metadataUrl = ipfsUrls[0];
        } else {
          metadataUrl = templateTokenURI;
        }

        console.log(`🌐 ERC1155 requesting metadata (no {id}):`, {
          contractAddress,
          tokenId,
          requestUrl: metadataUrl
        });

        metadataResponse = await fetch(metadataUrl, {
          signal: AbortSignal.timeout(5000)
        });

        if (!metadataResponse.ok) {
          throw new Error(`Failed to fetch metadata: ${metadataResponse.status}`);
        }
      }

      metadata = await metadataResponse.json();

      // Log ALL metadata responses
      console.log(`📄 METADATA JSON RESPONSE:`, metadata);
      console.log(`📄 IMAGE URL FROM METADATA:`, metadata.image);
    } catch (error) {
      console.warn(`Failed to fetch metadata from ${templateTokenURI}:`, error);
      return null;
    }

    // Extract the image URL from metadata
    const templateImageUrl = metadata.image;
    if (!templateImageUrl) {
      console.warn('No image field found in metadata');
      return null;
    }

    let fallbackImageUrl: string;

    // Handle ERC1155 vs ERC721 image URL logic
    if (isERC1155) {
      // For ERC1155: Check if the METADATA URL had {id} in it
      if (templateTokenURI.includes('{id}')) {
        // Metadata URL had {id}: Replace the end of image filename (same as ERC721 logic)
        const lastSlashIndex = templateImageUrl.lastIndexOf('/');
        if (lastSlashIndex === -1) {
          return null;
        }

        const basePath = templateImageUrl.substring(0, lastSlashIndex + 1);
        const filename = templateImageUrl.substring(lastSlashIndex + 1);

        // Replace the number in the filename with our token ID
        let newFilename: string;
        if (filename.includes('.')) {
          // Has extension (e.g., "51.png" -> "101.png")
          const extensionIndex = filename.lastIndexOf('.');
          const extension = filename.substring(extensionIndex);
          newFilename = tokenId + extension;
        } else {
          // No extension (e.g., "51" -> "101")
          newFilename = tokenId;
        }

        fallbackImageUrl = basePath + newFilename;

        console.log(`🔄 ERC1155 filename replacement (metadata had {id}):`, {
          originalUrl: templateImageUrl,
          tokenId,
          finalUrl: fallbackImageUrl
        });
      } else {
        // Metadata URL had NO {id}: Use image URL exactly as-is for all tokens
        fallbackImageUrl = templateImageUrl;

        console.log(`🔄 ERC1155 shared image (metadata had no {id}):`, {
          originalUrl: templateImageUrl,
          tokenId,
          finalUrl: fallbackImageUrl,
          note: 'All tokens use same image'
        });
      }
    } else {
      // ERC721: Handle standard numeric replacement in filename
      const lastSlashIndex = templateImageUrl.lastIndexOf('/');
      if (lastSlashIndex === -1) {
        return null;
      }

      const basePath = templateImageUrl.substring(0, lastSlashIndex + 1);
      const filename = templateImageUrl.substring(lastSlashIndex + 1);

      // Replace the number in the filename with our token ID
      let newFilename: string;
      if (filename.includes('.')) {
        // Has extension (e.g., "51.png" -> "101.png")
        const extensionIndex = filename.lastIndexOf('.');
        const extension = filename.substring(extensionIndex);
        newFilename = tokenId + extension;
      } else {
        // No extension (e.g., "51" -> "101")
        newFilename = tokenId;
      }

      fallbackImageUrl = basePath + newFilename;
    }

    // If it's an IPFS URI, convert to HTTP
    let finalUrl: string | null = fallbackImageUrl;
    if (fallbackImageUrl.startsWith('ipfs://')) {
      const ipfsUrls = convertIpfsToHttp(fallbackImageUrl);
      finalUrl = ipfsUrls[0] || null;
    }

    // Log ALL final image URLs
    console.log(`🖼️ FINAL IMAGE URL REQUESTED:`, finalUrl);

    // Verify the image URL works before caching
    if (finalUrl) {
      try {
        // Test if the image URL is accessible
        const imageResponse = await fetch(finalUrl, {
          method: 'HEAD', // Only check headers, don't download the image
          signal: AbortSignal.timeout(5000)
        });

        if (imageResponse.ok) {
          // Only cache if the image URL is accessible
          nftImageCache.set(contractAddress, tokenId, chainId, finalUrl);
          console.log(`✅ Image URL verified and cached:`, finalUrl);
        } else {
          console.warn(`❌ Image URL failed verification (${imageResponse.status}), not caching:`, finalUrl);
          return null; // Don't return broken URLs
        }
      } catch (error) {
        console.warn(`❌ Image URL failed verification, not caching:`, finalUrl, error);
        return null; // Don't return broken URLs
      }
    }

    return finalUrl;
  } catch (error) {
    console.warn('Failed to generate Blessnet contract fallback:', error);
    return null;
  }
}

/**
 * Check if we likely have a fast result (cached or non-contract based)
 */
export function hasFastImageFallback(
  primaryUrl?: string,
  contractAddress?: string,
  tokenId?: string,
  chainId?: number,
  tokenType?: string
): boolean {
  // If we have a primary URL, that's fast
  if (primaryUrl) return true;

  // If it's not a Blessnet chain, Alchemy fallbacks are fast
  const BLESSNET_MAINNET_ID = 45513;
  const BLESSNET_SEPOLIA_ID = 11145513;
  const isBlessnetChain = chainId === BLESSNET_MAINNET_ID || chainId === BLESSNET_SEPOLIA_ID;

  if (!isBlessnetChain) return true;

  // For Blessnet, check if we have it cached
  if (contractAddress && tokenId && chainId) {
    return nftImageCache.has(contractAddress, tokenId, chainId);
  }

  return false;
}

/**
 * Generates fallback image URLs from token metadata
 */
export async function generateImageFallbacks(
  primaryUrl?: string,
  tokenName?: string,
  tokenId?: string,
  contractAddress?: string,
  chainId?: number,
  tokenType?: string
): Promise<string[]> {
  const fallbacks: string[] = [];

  if (primaryUrl) {
    if (primaryUrl.includes('ipfs')) {
      // For IPFS URLs, add multiple gateway options
      fallbacks.push(...convertIpfsToHttp(primaryUrl));
    } else {
      // For regular URLs, just add the primary
      fallbacks.push(primaryUrl);
    }
  }

  // Chain-specific fallbacks
  const BLESSNET_MAINNET_ID = 45513;
  const BLESSNET_SEPOLIA_ID = 11145513;
  const isBlessnetChain = chainId === BLESSNET_MAINNET_ID || chainId === BLESSNET_SEPOLIA_ID;

  if (contractAddress && tokenId) {
    if (isBlessnetChain) {
      // For Blessnet chains, try to get tokenURI/uri from contract and construct fallback
      const contractFallback = await generateBlessnetContractFallback(
        contractAddress,
        tokenId,
        chainId!,
        tokenType
      );
      if (contractFallback) {
        fallbacks.push(contractFallback);
      }
    } else {
      // For other chains, use Alchemy's cached image service
      fallbacks.push(
        `https://res.cloudinary.com/alchemyapi/image/upload/mainnet/${contractAddress}/${tokenId}.png`
      );
    }
  }

  return fallbacks.filter(Boolean);
}

/**
 * Hook for handling image loading with multiple fallbacks
 */
export function useImageWithFallbacks(fallbackUrls: string[]) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const currentUrl = fallbackUrls[currentIndex];
  const hasMoreFallbacks = currentIndex < fallbackUrls.length - 1;

  const handleError = () => {
    if (hasMoreFallbacks) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsError(true);
      setIsLoading(false);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
    setIsError(false);
  };

  // Reset when URLs change
  useEffect(() => {
    setCurrentIndex(0);
    setIsError(false);
    setIsLoading(true);
  }, [fallbackUrls]);

  return {
    currentUrl,
    isError: isError && !hasMoreFallbacks,
    isLoading,
    handleError,
    handleLoad,
  };
}

/**
 * Preloads images to test which ones work
 * Only works in browser environment
 */
export async function findWorkingImageUrl(urls: string[]): Promise<string | null> {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // In server-side environment, just return the first URL
    return urls.length > 0 ? urls[0] : null;
  }

  for (const url of urls) {
    try {
      await new Promise((resolve, reject) => {
        // Use HTMLImageElement constructor to avoid conflicts with Next.js Image
        const img = document.createElement('img');
        img.onload = () => resolve(undefined);
        img.onerror = () => reject(new Error('Image failed to load'));
        img.src = url;
      });
      return url; // Return first working URL
    } catch {
      continue; // Try next URL
    }
  }
  return null; // No working URLs found
}

/**
 * Creates a data URL for a placeholder image with text
 * Only works in browser environment
 */
export function createPlaceholderDataUrl(
  text: string,
  width: number = 100,
  height: number = 100
): string {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // In server-side environment, return empty string
    return '';
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return '';

  canvas.width = width;
  canvas.height = height;

  // Background
  ctx.fillStyle = '#1f2937';
  ctx.fillRect(0, 0, width, height);

  // Text
  ctx.fillStyle = '#9ca3af';
  ctx.font = `${Math.min(width, height) / 6}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2);

  return canvas.toDataURL();
}
